import type { CSSProperties } from 'react';
import { useEffect, useState } from 'react';
import { Button, ButtonGroup } from 'react-bootstrap';

export const MODE_BUTTON_ACTIVE_COLOR = 'var(--color-primary-highlight)';
export const MODE_BUTTON_INACTIVE_COLOR = 'var(--color-primary-shadow)';

type ModeButtonStyle = CSSProperties & Record<string, string | number>;

export type ModeButtonProps<T extends string | number> = {
  options?: T[];
  onChange?: (value: T) => void;
  selectedOption?: T | null;
  enabled?: boolean;
  evenButtons?: boolean;
  activeStyle?: ModeButtonStyle;
  inactiveStyle?: ModeButtonStyle;
  buttonClassName?: string;
  wrapperClassName?: string;
  groupStyle?: CSSProperties;
  size?: 'sm' | 'md' | 'lg';
  parentControlledState?: boolean;
  ariaLabel?: string;
} & {};

const createModeButtonStyle = (
  backgroundColor: string,
  color: string,
  borderColor = 'transparent',
): ModeButtonStyle => ({
  backgroundColor,
  borderColor,
  color,
  '--bs-btn-bg': backgroundColor,
  '--bs-btn-border-color': borderColor,
  '--bs-btn-hover-bg': backgroundColor,
  '--bs-btn-hover-border-color': borderColor,
  '--bs-btn-active-bg': backgroundColor,
  '--bs-btn-active-border-color': borderColor,
  '--bs-btn-color': color,
  '--bs-btn-hover-color': color,
  '--bs-btn-active-color': color,
  '--bs-btn-disabled-color': color,
  '--bs-btn-disabled-bg': backgroundColor,
  '--bs-btn-disabled-border-color': borderColor,
  '--bs-btn-focus-shadow-rgb': '199 224 255',
});

const DEFAULT_ACTIVE_STYLE: ModeButtonStyle = createModeButtonStyle(
  MODE_BUTTON_ACTIVE_COLOR,
  'var(--color-primary-highlight-foreground)',
);
const DEFAULT_INACTIVE_STYLE: ModeButtonStyle = createModeButtonStyle(
  MODE_BUTTON_INACTIVE_COLOR,
  'var(--color-font)',
);

const ModeButton = <T extends string | number>({
  options = [],
  onChange = (_value: T): void => undefined,
  selectedOption = null,
  enabled = true,
  evenButtons = true,
  activeStyle = DEFAULT_ACTIVE_STYLE,
  inactiveStyle = DEFAULT_INACTIVE_STYLE,
  buttonClassName = '',
  wrapperClassName = '',
  groupStyle = {},
  size = 'md',
  parentControlledState = false,
  ariaLabel,
}: ModeButtonProps<T>): JSX.Element => {
  // React-Bootstrap only accepts `sm` and `lg`; `md` is normalized to the default button size.
  const normalizedButtonSize = size === 'md' ? undefined : size;
  const [selected, setSelected] = useState<T | null | undefined>(selectedOption ?? options[0]);

  useEffect(() => {
    if (parentControlledState) {
      setSelected(selectedOption);
    }
  }, [selectedOption, parentControlledState]);

  const updateSelection = (value: T): void => {
    if (!parentControlledState) {
      setSelected(value);
    }
    onChange(value);
  };

  const currentSelected = parentControlledState ? selectedOption : selected;

  return (
    <ButtonGroup
      aria-label={ariaLabel}
      className={`w-100 shadow-sm mode-button-group ${wrapperClassName}`}
      style={{
        border: '2px solid var(--color-primary-highlight)',
        borderRadius: 12,
        overflow: 'hidden',
        ...groupStyle,
      }}
    >
      {Array.isArray(options) &&
        options.map((option, index) => {
          const isActive = currentSelected === option;

          return (
            <Button
              key={`ModeButton#${index}`}
              className={`p-2 flex-fill fs-6 border-0 mode-button${
                index > 0 ? ' mode-button-sep' : ''
              } ${evenButtons ? 'flex-basis-0 min-w-0' : ''} ${buttonClassName} ${
                isActive ? 'mode-btn-active' : 'mode-btn-inactive'
              }`}
              variant={isActive ? 'primary' : 'outline-primary'}
              style={isActive ? activeStyle : inactiveStyle}
              onClick={() => updateSelection(option)}
              aria-pressed={isActive}
              disabled={!enabled}
              size={normalizedButtonSize}
            >
              {option}
            </Button>
          );
        })}
    </ButtonGroup>
  );
};

ModeButton.displayName = 'ModeButton';

export default ModeButton;
